// Services
import store from '../store'

// Utilities
import environment from './environmentUtils'
import isEmpty from './isEmpty'
import logger from './loggerUtil'
import { AnalyticsConstants, RegexConstants } from '../constants'

class SessionRecordUtil {
  /**
   * Sets the user ID in session recording integration.
   * @param {String|Number} identifier - The user specific variable to save (profile ID, email, phone number, application ID, customer ID, etc.).
   * @param {String} [prefix] - Optionally a prefix to include before the identifier value to differentiate from other types of identifiers.
   */
  static setUserIdentifier (identifier, prefix = undefined) {
    try {
      identifier = (identifier || '').toLowerCase()
      prefix = isEmpty(prefix) ? '' : `${prefix}_`

      const idValue = `${prefix}${identifier}`

      logger.debug(`Setting session record user identifier ${idValue}`)

      const csInstance = getCsInstance()
      const inspectInstance = getInspectInstance()

      csInstance.push(['trackPageEvent', `@user-identifier@${idValue}`])

      if (isEmpty(prefix)) { // Inspectlet only allows 1 user identifier per session so using profile ID only for now (which uses no prefix), the rest are tags
        inspectInstance.push(['identify', identifier])
      } else {
        const userIdKey = `user-id-${prefix}`
        inspectInstance.push(['tagSession', { [userIdKey]: identifier }])
      }
    } catch (error) {
      logger.error('Error setting session record user identifier')
      logger.debug(error)
    }
  }

  /**
   * Sets the user property in session recording integration. 40 distinct variables allowed per page view.
   * @param {String} key - The user property name (512 character max).
   * @param {String|Number} value - The user property value (255 character max).
   */
  static setUserProperty (key, value) {
    try {
      const csInstance = getCsInstance()
      const inspectInstance = getInspectInstance()

      csInstance.push(['trackDynamicVariable', { key, value }])
      inspectInstance.push(['tagSession', { [key]: value }])
    } catch (error) {
      logger.error('Error setting session record user info')
      logger.debug(error)
    }
  }

  /**
   * Sets user identity user properties after authentication.
   */
  static updateIdentity () {
    try {
      const vergentCustomerId = store.getters['customer/tlxeCustomerId']
      const cognitoId = store.getters['session/getCognitoId']
      const email = store.state.customer.email
      const phoneNumber = store.state.customer.phoneNumber?.phoneNumber?.replaceAll(RegexConstants.phoneNumberFormatting, '').slice(-10) // must be numbers only for Content Square (and remove potential country code)

      SessionRecordUtil.setUserIdentifier(cognitoId)
      SessionRecordUtil.saveInitUserProperties()

      if (!isEmpty(vergentCustomerId)) {
        SessionRecordUtil.setUserIdentifier(vergentCustomerId, AnalyticsConstants.propName.mobileTLXeCustomerId)
      }

      if (!isEmpty(email)) {
        SessionRecordUtil.setUserIdentifier(email, AnalyticsConstants.propName.email)
      }

      if (!isEmpty(phoneNumber)) {
        SessionRecordUtil.setUserIdentifier(phoneNumber, AnalyticsConstants.propName.phone)
      }

      SessionRecordUtil.setUserProperty(AnalyticsConstants.propName.isMobileGenerated, 'true')
    } catch (error) {
      logger.error('Error setting update identity session record user properties - updateIdentity.')
      logger.debug(error)
    }
  }

  /**
   * Sets the site-wide, initialization related user properties.
   */
  static saveInitUserProperties () {
    SessionRecordUtil.setUserProperty(AnalyticsConstants.propName.environment, environment.environment)
    SessionRecordUtil.setUserProperty(AnalyticsConstants.propName.siteBrand, process.env.VUE_APP_AppBrand)

    if (store.state.display.isPwa) {
      SessionRecordUtil.setUserProperty(true, AnalyticsConstants.propName.isPwa)
    }
  }

  /**
   * Records a single artificial page view into session recording integration. The integration automatically tracks full page reloads which do not happen in SPAs.
   * @param {String} pageView - The vue router name property being navigated to.
   * @param {String} skipCsTracking - If true, the view will not be recorded into ContentSquare. This is needed due to this being a SPA and ContentSquare records the first page on load.
   */
  static logPageView (pageView, skipCsTracking = false) {
    try {
      const csInstance = getCsInstance()
      const inspectInstance = getInspectInstance()

      if (!skipCsTracking) {
        csInstance.push(['trackPageview', `${globalThis.location.pathname}${globalThis.location.hash.replace('#', 'csp')}`]) // Prevent ContentSquare from stripping off path names after hash mark
      }

      inspectInstance.push(['tagSession', { pageView }])
    } catch (error) {
      logger.error('Error logging session record page view event')
      logger.debug(error)
    }
  }
}

export default SessionRecordUtil

const getCsInstance = () => {
  globalThis._uxa = globalThis._uxa ?? []

  return globalThis._uxa
}

const getInspectInstance = () => {
  globalThis.__insp = globalThis.__insp ?? []

  return globalThis.__insp
}
